import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import {
  selectPlaygroundLogsFilters,
  setPlaygroundChangesMadeOn,
} from "../../../redux/slices/filterSlice";
import PlaygroundIcon from "../../../assets/svg/playground-icon";
import CheckedIcon from "../../../assets/svg/checked-icon";
import { RootState } from "../../../redux/store";
import { getRegionLabel } from "../../../utils/constants/mappings";

const PlaygroundEnvFilter = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const { changesMadeOn } = useSelector(selectPlaygroundLogsFilters);
  const clientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const [selectedEnv, setSelectedEnv] = useState<string>(
    (changesMadeOn.value as string) || "all"
  );

  const ENVIRONMENT_OPTIONS = useMemo(
    () => [
      {
        value: "live",
        label: "Production",
        icon: CheckedIcon,
        iconClass:
          "w-[14px] h-[14px] text-verba-green-success dark:text-verba-green-success-dk",
      },
      {
        value: "playground",
        label: getRegionLabel("playground", clientCode),
        icon: PlaygroundIcon,
        iconClass:
          "w-[18px] h-[18px] text-tag-scheduled dark:text-tag-scheduled-dk",
      },
    ],
    [clientCode]
  );

  const handleApply = () => {
    if (selectedEnv === changesMadeOn.value) {
      onClose();
      return;
    }

    const displayValue =
      ENVIRONMENT_OPTIONS.find((option) => option.value === selectedEnv)
        ?.label || selectedEnv;

    dispatch(
      setPlaygroundChangesMadeOn({
        value: selectedEnv,
        displayValue,
      })
    );
    onClose();
  };

  return (
    <div className="flex flex-col gap-2 mt-2 px-3">
      <h2 className="text-md text-heading-text dark:text-heading-text-dk py-1.5">
        Choose the environment to view logs from.
      </h2>
      {ENVIRONMENT_OPTIONS.map((option) => {
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            onClick={() => setSelectedEnv(option.value)}
            className={`flex flex-col gap-1 border rounded-lg text-left px-3 py-3 transition-colors ${
              selectedEnv === option.value
                ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
                : "border-border-gray dark:border-border-gray-dk"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex gap-1.5 items-center">
                {Icon && <Icon className={option.iconClass} />}
                <p className="text-md font-semibold text-heading-text dark:text-heading-text-dk capitalize">
                  {option.label}
                </p>
              </div>
              {selectedEnv === option.value && (
                <CheckIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
              )}
            </div>
          </button>
        );
      })}
      <div className="flex justify-end gap-2 mt-4">
        <Button
          innerText="Cancel"
          onClick={onClose}
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
        />
        <Button
          innerText="Apply"
          onClick={handleApply}
          disabled={selectedEnv === changesMadeOn.value}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </div>
  );
};

export default PlaygroundEnvFilter;
